import { getProjectOrganisationIds } from '@resistapp/common/access';
import type { AdminUserDirectoryUser } from '@resistapp/common/api-types';
import { AllProjectSampleTypesGroup } from '@resistapp/common/comparable-sample-groups';
import { getFriendlyInterval } from '@resistapp/common/date-utils';
import { Feature } from '@resistapp/common/features';
import { DEFAULT_END_INTERVAL, DEFAULT_START_INTERVAL } from '@resistapp/common/friendly';
import { buildOrganisationInheritanceSnapshotById } from '@resistapp/common/organisation-inheritance';
import { FullProject, OrganisationSummary, SampleStatus, SmartChipFlag } from '@resistapp/common/types';
import { values } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { LocalStorageId } from '../components/shared/local-storage';
import { useSampleDataContext } from '../contexts/sample-data-context';
import { useUser } from '../contexts/use-user-context';
import { getResearchAdminHighlightFlag, StoredChipViewHighlightValue } from '../utils/chip-view-highlight-mode';
import { useAdminUserDirectory, useOrganisations, usePatchProjectSamples, usePatchSampleStatuses } from './api';
import { useFilters } from './use-query-filters/use-query-filters';

export interface ResearchAdminFeatures {
  updateSampleStatuses: (newStatus: SampleStatus, samplingPointId?: number) => void;
  isModifyAsAdminActive: boolean;
  setIsModifyAsAdminActive: (value: boolean) => void;
  project: FullProject | null;
  patchSamplesToProject: ReturnType<typeof usePatchProjectSamples>;
  organisationUsersByOrganisationId: ReadonlyMap<number, AdminUserDirectoryUser[]>;
  organisationsWithAccess: OrganisationSummary[];
  organisationsWithoutAccess: OrganisationSummary[];
  highlightFlag: SmartChipFlag | null;
  setHighlightFlag: (flag: SmartChipFlag | null) => void;
  getAccessDifferences: (targetProjectId: number) => {
    usersOnlyInTarget: AdminUserDirectoryUser[];
    usersOnlyInCurrent: AdminUserDirectoryUser[];
    orgsOnlyInTarget: OrganisationSummary[];
    orgsOnlyInCurrent: OrganisationSummary[];
  };
}

/**
 * Whether the admin "modify" mode is active for the logged in admin user.
 * Lightweight alternative to useResearchAdmin for components that only need this flag.
 */
export function useIsModifyAsAdminActive(): [boolean, (value: boolean) => void] {
  const { isAdmin: loggedInUserIsAdmin } = useUser();
  const [storedIsModifyAsAdminActive, setIsModifyAsAdminActive] = useLocalStorageState<boolean>(
    LocalStorageId.adminMode,
    {
      defaultValue: false,
    },
  );
  return [storedIsModifyAsAdminActive && loggedInUserIsAdmin, setIsModifyAsAdminActive];
}

export function useResearchAdmin(): ResearchAdminFeatures {
  const { data: project, refetchData } = useSampleDataContext();
  const { data: userDirectory, send: loadAdminUserDirectoryStable } = useAdminUserDirectory();
  const { data: allOrganisations } = useOrganisations();
  const filters = useFilters();
  const { isAdmin: loggedInUserIsAdmin } = useUser();
  const patchSamplesToProject = usePatchProjectSamples();
  const [isModifyAsAdminActive, setIsModifyAsAdminActive] = useIsModifyAsAdminActive();

  // Persist highlight flag selection in local storage (shared with Chip View)
  const [storedHighlightFlag, setStoredHighlightFlag] = useLocalStorageState<StoredChipViewHighlightValue>(
    LocalStorageId.chipViewHighlightFlag,
    {
      defaultValue: 'None',
    },
  );

  const highlightFlag = getResearchAdminHighlightFlag(storedHighlightFlag);

  const setHighlightFlag = useCallback(
    (flag: SmartChipFlag | null) => {
      setStoredHighlightFlag(flag ?? 'None');
    },
    [setStoredHighlightFlag],
  );

  const { mutate: patchSampleStatuses } = usePatchSampleStatuses(
    () => {
      refetchData();
    },
    (err: Error) => {
      console.error('Failed to update sample statuses:', err);
    },
  );

  const { selectedSampleTypeGroup, interval } = filters;
  const updateSampleStatuses = useCallback(
    (newStatus: SampleStatus, samplingPointId?: number) => {
      if (!project?.focusedByUID) return;
      const focusedSamples = values(project.focusedByUID).flat();
      if (!samplingPointId) {
        const envType =
          selectedSampleTypeGroup === AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS
            ? ''
            : `${selectedSampleTypeGroup} samples`;

        const intervalText =
          interval.start.getTime() === DEFAULT_START_INTERVAL.getTime() &&
          interval.end.getTime() === DEFAULT_END_INTERVAL.getTime()
            ? ''
            : ` (${getFriendlyInterval([{ date: interval.start }, { date: interval.end }])})`;
        const allSamples = values(project.samplesByUID).flat();
        const confirmationText =
          focusedSamples.length === allSamples.length
            ? `Setting status ${newStatus} for all ${allSamples.length} project samples.\n\nAre you sure?`
            : `Setting status ${newStatus} for ${focusedSamples.length} ${envType}${intervalText}\nout of all ${allSamples.length} project samples.\n\nAre you sure?`;
        const confirmed = window.confirm(confirmationText);
        if (!confirmed) return;
      }
      const newStatuses = focusedSamples
        .filter(data => (samplingPointId ? data.samplingPoint.id === samplingPointId : true))
        .map(sample => {
          return { id: sample.id, status: newStatus };
        });
      patchSampleStatuses(newStatuses);
    },
    [project, patchSampleStatuses, selectedSampleTypeGroup, interval],
  );

  const organisationInheritanceById = useMemo(
    () => buildOrganisationInheritanceSnapshotById(allOrganisations ?? []),
    [allOrganisations],
  );

  const adminUserIds = useMemo(() => {
    if (!userDirectory || !allOrganisations) {
      return new Set<number>();
    }
    const adminIds = userDirectory.organisationUsers
      .filter(membership =>
        organisationInheritanceById.get(membership.organisationId)?.features.includes(Feature.ADMIN),
      )
      .map(membership => membership.userId);

    return new Set(adminIds);
  }, [allOrganisations, organisationInheritanceById, userDirectory]);

  const organisationUsersByOrganisationId = useMemo(() => {
    const currentProjectId = project?.id;
    if (!userDirectory || !currentProjectId) {
      return new Map<number, AdminUserDirectoryUser[]>();
    }

    const usersById = new Map(userDirectory.users.map(user => [user.id, user] as const));
    const userIdsWithAccessToProject = new Set<number>();

    userDirectory.organisationUsers.forEach(membership => {
      if (adminUserIds.has(membership.userId)) {
        return;
      }
      const orgSnapshot = organisationInheritanceById.get(membership.organisationId);
      if (orgSnapshot?.projectAccesses.includes(currentProjectId)) {
        userIdsWithAccessToProject.add(membership.userId);
      }
    });

    const usersByOrganisationId = new Map<number, AdminUserDirectoryUser[]>();

    userDirectory.organisationUsers.forEach(membership => {
      if (!userIdsWithAccessToProject.has(membership.userId)) {
        return;
      }
      const user = usersById.get(membership.userId);
      if (!user) {
        return;
      }
      const existing = usersByOrganisationId.get(membership.organisationId);
      if (existing) {
        existing.push(user);
        return;
      }
      usersByOrganisationId.set(membership.organisationId, [user]);
    });

    return new Map(
      Array.from(usersByOrganisationId.entries(), ([orgId, users]) => [
        orgId,
        users.slice().sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)),
      ]),
    );
  }, [adminUserIds, organisationInheritanceById, project?.id, userDirectory]);

  const organisationsWithAccess = useMemo(
    () => allOrganisations?.filter(org => getProjectOrganisationIds(org).includes(project?.id ?? -1)) || [],
    [allOrganisations, project?.id],
  );

  const organisationsWithoutAccess = useMemo(
    () => allOrganisations?.filter(org => !getProjectOrganisationIds(org).includes(project?.id ?? -1)) || [],
    [allOrganisations, project?.id],
  );

  const getAccessDifferences = useCallback(
    (targetProjectId: number) => {
      if (!userDirectory || !allOrganisations || !project) {
        return {
          usersOnlyInTarget: [] as AdminUserDirectoryUser[],
          usersOnlyInCurrent: [] as AdminUserDirectoryUser[],
          orgsOnlyInTarget: [] as OrganisationSummary[],
          orgsOnlyInCurrent: [] as OrganisationSummary[],
        };
      }

      const currentProjectId = project.id;

      const orgsWithAccessToCurrent = allOrganisations.filter(org =>
        getProjectOrganisationIds(org).includes(currentProjectId),
      );
      const orgsWithAccessToTarget = allOrganisations.filter(org =>
        getProjectOrganisationIds(org).includes(targetProjectId),
      );

      const orgIdsWithAccessToCurrent = new Set(orgsWithAccessToCurrent.map(org => org.id));
      const orgIdsWithAccessToTarget = new Set(orgsWithAccessToTarget.map(org => org.id));

      // Compare organisation access rights
      const orgsOnlyInTarget = orgsWithAccessToTarget.filter(org => !orgIdsWithAccessToCurrent.has(org.id));
      const orgsOnlyInCurrent = orgsWithAccessToCurrent.filter(org => !orgIdsWithAccessToTarget.has(org.id));

      const usersById = new Map(userDirectory.users.map(user => [user.id, user] as const));
      const userIdsWithAccessToCurrent = new Set<number>();
      const userIdsWithAccessToTarget = new Set<number>();

      userDirectory.organisationUsers.forEach(membership => {
        if (adminUserIds.has(membership.userId)) {
          return;
        }
        const orgSnapshot = organisationInheritanceById.get(membership.organisationId);
        if (!orgSnapshot) {
          return;
        }
        if (orgSnapshot.projectAccesses.includes(currentProjectId)) {
          userIdsWithAccessToCurrent.add(membership.userId);
        }
        if (orgSnapshot.projectAccesses.includes(targetProjectId)) {
          userIdsWithAccessToTarget.add(membership.userId);
        }
      });

      const usersOnlyInTarget = Array.from(userIdsWithAccessToTarget)
        .filter(userId => !userIdsWithAccessToCurrent.has(userId))
        .map(userId => usersById.get(userId))
        .filter((user): user is AdminUserDirectoryUser => user !== undefined);

      const usersOnlyInCurrent = Array.from(userIdsWithAccessToCurrent)
        .filter(userId => !userIdsWithAccessToTarget.has(userId))
        .map(userId => usersById.get(userId))
        .filter((user): user is AdminUserDirectoryUser => user !== undefined);

      return {
        usersOnlyInTarget,
        usersOnlyInCurrent,
        orgsOnlyInTarget,
        orgsOnlyInCurrent,
      };
    },
    [adminUserIds, allOrganisations, project, userDirectory],
  );

  useEffect(() => {
    if (loggedInUserIsAdmin) {
      void loadAdminUserDirectoryStable();
    }
  }, [loggedInUserIsAdmin]);

  return {
    updateSampleStatuses,
    isModifyAsAdminActive,
    setIsModifyAsAdminActive,
    project: project || null,
    patchSamplesToProject,
    organisationUsersByOrganisationId,
    organisationsWithAccess,
    organisationsWithoutAccess,
    highlightFlag,
    setHighlightFlag,
    getAccessDifferences,
  };
}
